import {createInjectionState, until, useElementSize, useEventListener} from '@vueuse/core';
import {
    computed,
    watch,
    type ComputedRef,
    type Ref,
    watchEffect,
    shallowRef,
    triggerRef,
} from 'vue';
import panzoom, {type Transform, type PanZoom} from 'panzoom';
import {recomputeAllPoppers} from 'floating-vue';
import {throttle} from 'es-toolkit';
import {clamp} from '../../helpers/numberHelper';
import {getMapBaseSize} from '../../helpers/mapHelper';
import {useBreakpoint} from '../../composables/useBreakpoints';

/**
 * Zoom steps beteween min and max zoom levels.
 * Each increment of 1 results in doubling the size of the map.
 */
const zoomSteps = 3;

/**
 * Zoom factor that gets multiplied with the minimum zoom value.
 */
const additionalMinZoomFactor = 0.9;

/**
 * Ways to interact with the panZoom object
 */
export type MapInteractionModes = 'zoom' | 'pan';

export type UseMapStateOptions = {
    globalContainerSize: Ref<{width: Ref<number>; height: Ref<number>}>;
    element: Ref<HTMLElement | null>;
    dimensions: ComputedRef<{height: number; width: number}>;
    rightPadding: ComputedRef<number>;
    allowedInteractions: ComputedRef<MapInteractionModes[]>;
    popoverBoundary: Ref<HTMLElement | null>;
    iconScale: Ref<number>;
};

const [useProvideStore, useInjectStore] = createInjectionState(
    ({
        globalContainerSize,
        element,
        dimensions,
        rightPadding,
        allowedInteractions,
        popoverBoundary,
        iconScale,
    }: UseMapStateOptions) => {
        const panZoom = shallowRef<PanZoom>();

        const transform = shallowRef<Transform>();

        const elementContainer = computed(() => element.value?.parentElement);
        const {width, height} = useElementSize(elementContainer);

        /**
         * Panzoom only initialises its pinch baseline when a two-finger touch
         * starts on the map. We gate the pinch on that same condition
         * so the baseline always exists before panzoom reads it.
         */
        let pinchReady = false;

        useEventListener(
            element,
            'touchstart',
            (e: TouchEvent) => {
                if (e.touches.length === 2) {
                    pinchReady = true;
                }
            },
            {capture: true}
        );

        useEventListener(
            document,
            'touchmove',
            (e: TouchEvent) => {
                // Block the pinch until panzoom's baseline is initialised. We
                // do NOT preventDefault, so native scrolling still works.
                if (e.touches.length >= 2 && !pinchReady) {
                    e.stopPropagation();
                }
            },
            {capture: true}
        );

        useEventListener(
            document,
            ['touchend', 'touchcancel'],
            (e: TouchEvent) => {
                if (e.touches.length < 2) {
                    pinchReady = false;
                }
            },
            {capture: true}
        );

        const minZoom = computed(() => {
            const {width: imageWidth, height: imageHeight} = dimensions.value;

            if (!imageHeight || !imageWidth || !height.value || !width.value) {
                return 1;
            }

            const heightScale = height.value / imageHeight;
            const widthScale = (width.value - rightPadding.value) / imageWidth;
            return Math.min(heightScale, widthScale) * additionalMinZoomFactor;
        });

        const maxZoom = computed(() => minZoom.value + zoomSteps);

        /**
         * For larger screens and maps, we increase the base size of markers
         * to maintain legibility and avoid manual size adjustments
         */
        const baseZoomLevel = computed(() => {
            const {width: imageWidth, height: imageHeight} = dimensions.value;
            return getMapBaseSize(imageWidth, imageHeight) * iconScale.value;
        });

        watch(element, async elementValue => {
            if (!elementValue) {
                return;
            }

            const panZoomObject = panzoom(elementValue, {
                minZoom: minZoom.value,
                maxZoom: maxZoom.value,
                initialZoom: minZoom.value,
                smoothScroll: true,
                bounds: true,
                boundsPadding: 0.2,
                initialX: 0,
                initialY: 0,
                beforeWheel() {
                    return !allowedInteractions.value.includes('zoom');
                },
                onTouch() {
                    return false;
                },
                beforeMouseDown(e) {
                    return e.shiftKey;
                },
            });

            panZoom.value = panZoomObject;

            watch(allowedInteractions, value => {
                if (!value.length) {
                    panZoomObject.pause();
                    return;
                }
                panZoomObject.resume();
            });

            panZoomObject.on(
                'transform',
                throttle(() => {
                    recomputeAllPoppers();
                    transform.value = panZoomObject.getTransform();
                    triggerRef(transform);
                }, 10)
            );

            const {initialPanX, initialPanY} = initialPanning.value;

            watchEffect(() => {
                panZoomObject.setMinZoom(minZoom.value);
                panZoomObject.setMaxZoom(maxZoom.value);
                panZoomObject.zoomAbs(0, 0, minZoom.value);
                panZoomObject.moveTo(initialPanX, initialPanY);
            });
        });

        const {breakpointLgAndGreater} = useBreakpoint();

        watch(breakpointLgAndGreater, () => {
            recomputeAllPoppers();
        });

        function changeZoom(direction: 'in' | 'out') {
            if (!transform.value || !Number.isFinite(transform.value.scale)) {
                return;
            }
            const zoomBy = direction === 'in' ? 1.5 : 1 / 1.5;
            const targetScale = transform.value.scale * zoomBy;
            if (targetScale < minZoom.value || isApproximatelyEqual(targetScale, minZoom.value)) {
                zoomOutMax();
                return;
            }
            const rect = elementContainer.value!.getBoundingClientRect();
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            if (
                !Number.isFinite(centerX) ||
                !Number.isFinite(centerY) ||
                !Number.isFinite(zoomBy)
            ) {
                return;
            }

            panZoom.value?.smoothZoom(centerX, centerY, zoomBy);
        }

        const initialPanning = computed(() => {
            const mapSize = useElementSize(element);

            const containerWidth = globalContainerSize.value.width.value;
            const containerHeight = globalContainerSize.value.height.value;

            const mapHeight = mapSize.height.value;
            const mapWidth = mapSize.width.value;

            const containerRatio = containerWidth / containerHeight;
            const mapRatio = mapWidth / mapHeight;

            let scaledWidth, scaledHeight;

            if (mapRatio > containerRatio) {
                scaledWidth = containerWidth;
                scaledHeight = containerWidth / mapRatio;
            } else {
                scaledHeight = containerHeight;
                scaledWidth = containerHeight * mapRatio;
            }

            const initialPanX = (containerWidth - scaledWidth * additionalMinZoomFactor) / 2;
            const initialPanY = (containerHeight - scaledHeight * additionalMinZoomFactor) / 2;

            return {initialPanX, initialPanY};
        });

        // Sometimes it's not possible to actually zoom to the exact min/max zoom level.
        // For these cases its good enough to determine whether the values are close to another.
        function isApproximatelyEqual(num1?: number, num2?: number): boolean {
            if (typeof num1 === 'undefined' || typeof num2 === 'undefined') {
                return false;
            }
            return Math.abs(num1 - num2) < 0.1 * minZoom.value;
        }

        const isZoomOutDisabled = computed(() =>
            isApproximatelyEqual(transform.value?.scale, minZoom.value)
        );

        const isZoomInDisabled = computed(() =>
            isApproximatelyEqual(transform.value?.scale, maxZoom.value)
        );

        const isMaxZoomDisabled = computed(
            () => isZoomOutDisabled.value && transform.value?.x === 0 && transform.value.y === 0
        );

        function zoomOutMax() {
            const {initialPanX, initialPanY} = initialPanning.value;

            panZoom.value?.zoomAbs(0, 0, 0);
            panZoom.value?.moveTo(initialPanX, initialPanY);
        }

        async function zoomTo(
            clientX: number,
            clientY: number,
            zoom?: number,
            additionalRightPan?: number
        ) {
            await until(panZoom).not.toBeUndefined();
            await until(minZoom).not.toBeNull();

            await until(() => width.value).not.toBe(0);
            await until(() => height.value).not.toBe(0);

            zoom = transform.value!.scale;

            const zoomFactor = clamp(zoom, minZoom.value, maxZoom.value);

            // the additional right pan does not get affected by the zoom factor to be able to align to surronded elements
            const offsetX = width.value / 2 - clientX * zoomFactor - (additionalRightPan ?? 0);
            const offsetY = height.value / 2 - clientY * zoomFactor;

            panZoom.value?.smoothMoveTo(offsetX, offsetY);
        }

        return {
            panZoom,
            elementContainer,
            changeZoom,
            zoomOutMax,
            zoomTo,
            isZoomOutDisabled,
            isZoomInDisabled,
            isMaxZoomDisabled,
            baseZoomLevel,
            transform,
            popoverBoundary,
        };
    }
);

export const provideMapState = useProvideStore;

export function injectMapState() {
    const store = useInjectStore();
    if (store == null)
        throw new Error('Please call `provideStore` on the appropriate parent component');
    return store;
}
